import React, {useContext} from 'react';
import _ from "lodash";
import {useModelFormEngine} from "../common/hooks/useModelFormEngine";
import {ApiUtility} from "../common/ApiUtility";
import {Constants} from "../common/Constants";
import {ActivityScope} from "../../types/ActivityScope";
import {ActivityMaterial} from "../../types/ActivityMaterial";
import {CheckBox} from "../common/CheckBox";
import {CheckBoxSet} from "../common/CheckBoxSet";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {PillButton} from "../common/PillButton";
import {LeaveWithoutSavingWarningModal} from "../common/LeaveWithoutSavingWarningModal";
import {GlobalContext} from "../admin-frame/AdminFrame";

interface ActivityScopeFormProps {
    activityScope?: ActivityScope;
    save: Function;
    isNew?: boolean;
}

interface ActivityScopeFormState {
    name: string;
    active: boolean;
    activity_material_ids: number[];
    activityMaterialsList: ActivityMaterial[];
    loading: boolean;
}

export let ActivityScopeForm = function (props: ActivityScopeFormProps) {
    const context = useContext(GlobalContext);
    const activityScope = _.clone(props.activityScope) || undefined;
    const scopesListPath = `/${Constants.routes.settings.pathSegment}/activity-scopes`;

    const initialModelState: ActivityScopeFormState = {
        name: activityScope?.name || '',
        active: activityScope?.active ?? true,
        activity_material_ids: activityScope?.activity_material_ids || [],
        activityMaterialsList: [],
        loading: false,
    };

    const formEngine = useModelFormEngine(
        initialModelState, activityScope, fetchSupportingData, validateForm, formStateToSaveData, props.save
    );

    function fetchSupportingData() {
        return ApiUtility.getActivityMaterials().then(function (activityMaterialsList: ActivityMaterial[]) {
            return {activityMaterialsList: activityMaterialsList};
        });
    }

    function validateForm(handleValidationResult: Function) {
        return new Promise(function (resolve) {
            let validationErrors = [];
            let invalidFields = [];

            if (!formEngine.dataStore.data().name) {
                validationErrors.push('Please enter a scope name.');
                invalidFields.push('name');
            }

            return handleValidationResult(validationErrors, invalidFields, resolve);
        });
    }

    function formStateToSaveData(formState: ActivityScopeFormState) {
        let saveData: { id?: number; name: string; active: boolean; activity_material_ids: number[] } = {
            name: formState.name,
            active: formState.active,
            activity_material_ids: formState.activity_material_ids,
        };
        if (activityScope) {
            saveData.id = activityScope.id;
        }
        return saveData;
    }

    const materialOptions = formEngine.dataStore.data().activityMaterialsList.map(function (activityMaterial: ActivityMaterial) {
        const inactiveSuffix = activityMaterial.active ? '' : ' (Inactive)';
        return {value: activityMaterial.id, label: `${activityMaterial.name}${inactiveSuffix}`, inactive: !activityMaterial.active};
    });
    const checkedMaterialIds = formEngine.dataStore.data().activity_material_ids;
    const allMaterialsChecked = materialOptions.length > 0 && materialOptions.every(function (materialOption) {
        return checkedMaterialIds.includes(materialOption.value);
    });

    function onChangeCheckedMaterials(updatedCheckedMaterialIds: number[]) {
        formEngine.updateAndRenderUnsavedData({activity_material_ids: updatedCheckedMaterialIds});
    }

    function onChangeSelectAllMaterials(checked: boolean) {
        formEngine.updateAndRenderUnsavedData({
            activity_material_ids: checked ? materialOptions.map((materialOption) => materialOption.value) : [],
        });
    }

    return (
        <div className="common-form-container">
            <ConditionalRenderer
                if={formEngine.dataStore.data().loading}
                renderer={() => (<OverlayLoadingIndicator/>)}
            />
            <div className="common-form narrow-form settings-form activity-scope-form">
                <div className="settings-form-back-link" onClick={() => context.navigator.navigateTo(scopesListPath)}>
                    <img className="settings-form-back-link-chevron" src="/static/images/chevron-left-textcolor.svg"/>
                    BACK TO SCOPES
                </div>
                <div className="settings-form-title">{props.isNew ? "Add Scope" : "Edit Scope"}</div>
                <div className="common-form-body">
                    <div className="common-form-body-row">
                        <div className={formEngine.getFormFieldClasses("common-form-field", "name")}>
                            <div className="common-form-field-label">Scope Name</div>
                            <div className="common-form-field-input-container">
                                <input
                                    id="name"
                                    type="text"
                                    className="common-form-input"
                                    value={formEngine.dataStore.data().name}
                                    onChange={(event) => formEngine.handleTextFieldChange("name", event)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="settings-form-divider"></div>
                    <div className="common-form-body-row settings-form-checkbox-section">
                        <div className="settings-form-checkbox-section-header">
                            <div className="common-form-field-label">MATERIALS</div>
                            <div className="common-form-checkbox-set-select-all">
                                <CheckBox
                                    id="activityScopeMaterialsSelectAll"
                                    checked={allMaterialsChecked}
                                    onChange={onChangeSelectAllMaterials}
                                    label="SELECT ALL"
                                />
                            </div>
                        </div>
                        <div className="settings-form-checkbox-section-help-text">
                            Checked materials display when this scope is selected on a dispatch order.
                        </div>
                        <div className="settings-form-checkbox-set-container">
                            <CheckBoxSet
                                options={materialOptions}
                                values={checkedMaterialIds}
                                onChange={onChangeCheckedMaterials}
                            />
                        </div>
                    </div>
                    <div className="settings-form-divider"></div>
                    <div className="common-form-body-row active-checkbox-row">
                        <CheckBox
                            id="activity-scope-form-active-checkbox"
                            checked={formEngine.dataStore.data().active}
                            onChange={(checked: boolean) => formEngine.handleCheckboxChange("active", checked)}
                            label="Active"
                        />
                    </div>
                    <div className="common-form-body-row">
                        <div className="common-form-action-buttons-row">
                            <PillButton
                                containerClasses="common-form-action-button-container"
                                buttonClasses="solid-green-button submit-button"
                                onClick={formEngine.onClickSave}
                                buttonText={props.isNew ? "CREATE" : "SAVE"}
                            />
                            <PillButton
                                containerClasses="common-form-action-button-container"
                                buttonClasses="green-border-button cancel-button"
                                onClick={() => context.navigator.navigateTo(scopesListPath)}
                                buttonText="CANCEL"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <LeaveWithoutSavingWarningModal navigationBlocker={formEngine.navigationBlocker}/>
        </div>
    );
};
